/**
 * 3D Room Visualizer — bare areas.
 *
 * What a wall or ceiling shows where no product has been put on it. Left
 * alone those areas were see-through (a wall with no sections drew nothing
 * but its framing, a bare ceiling nothing at all). The Materials window
 * chooses, for walls and for ceilings separately (renderSettings
 * `bareWallFill` / `bareCeilingFill`):
 *
 *   studs    the 2x4s of the wall where it is bare — whether or not the
 *            framing is shown — with the paper back of a sheet of drywall
 *            behind them, so the room never looks straight through;
 *   drywall  unpainted drywall: mudded, taped joints and screw dimples;
 *   grid     a neutral grey grid, to try other fills against.
 *
 * A bare area is the surface's rectangle less its sections (each the box of
 * its boards) and its windows and doors (`subtractRects`, the same cut the
 * 2D editor makes). Everything is built in the wall's canonical front-face
 * coordinates — x along, y up, the board plane at z = 0, the framing in
 * z < 0 — with positions baked into the vertices, because the left and
 * right walls are placed by reflecting vertices, not by moving meshes
 * (extrudedBoardBuilder.applyFaceTransformToGroup). The group goes into
 * the wall's own group, so it fades, locks and exports with the wall.
 *
 * The studs ARE the room's framing (framingBuilder.buildWallBareFraming: the
 * same plates, king studs, headers, sills and cripples), cut to the bare
 * areas. When the framing is shown the real one stands there already, so the
 * bare areas' copy (`userData.bareStuds`) is hidden then; the viewer toggles
 * it with the framing. The sheets stay either way.
 *
 * The sheets close the box: a wall's sheet runs on up past the wall's top to
 * meet the ceiling's, and the ceiling's reaches out past the room's edges to
 * meet the walls', so there is no slot to see out through where they meet.
 *
 * The textures are drawn on canvases, tiled in feet, and shared; materials
 * are made per wall (each wall's opacity is its own).
 */

import * as THREE from 'three';
import { Rect, subtractRects } from '../../../calculations/sections/resolver';
import { createLumberMaterial } from './materialFactory';
import { grainOffset, setGrainUVs } from './woodTexture';
import { buildWallBareFraming } from './framingBuilder';
import { applyFaceTransformToGroup } from './extrudedBoardBuilder';
import type { BoardRect, CeilingBoardLayout, WallBoardLayout, WallFace } from './types';
import type { BareFillId } from './renderSettings';

const STUD_THICKNESS = 1.5 / 12;
const STUD_DEPTH = 3.5 / 12;
const PLATE_THICKNESS = 1.5 / 12;
const STUD_SPACING = 16 / 12;
/** Narrower or shorter than this (half an inch) and a bare strip is not drawn. */
const MIN_BARE = 0.04;
const MIN_STICK = 0.02;
const EPS = 1e-4;
/** The sheet behind the studs: just outside them. */
const BACKING_OFFSET = STUD_DEPTH + 0.01;
/** How far a wall's sheet runs on above the wall, and a ceiling's out past the room, so the two meet. */
const RIM = BACKING_OFFSET + 0.02;

export const BARE_FILL_IDS: BareFillId[] = ['studs', 'drywall', 'grid'];

// ---------------------------------------------------------------------------
// Where the bare areas are
// ---------------------------------------------------------------------------

function boxOf(boards: BoardRect[]): Rect | null {
  if (boards.length === 0) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const b of boards) {
    x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width); y1 = Math.max(y1, b.y + b.height);
  }
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

const bigEnough = (r: Rect) => r.width >= MIN_BARE && r.height >= MIN_BARE;

/**
 * A wall's bare rectangles, in its surface coordinates: the wall less each
 * section's extent (its boards' span along the wall, the section's own
 * height) and each window or door.
 */
export function bareRects(layout: WallBoardLayout): Rect[] {
  const whole: Rect = { x: 0, y: 0, width: layout.wallLength, height: layout.wallHeight };
  const holes: Rect[] = [];
  for (const s of layout.sections) {
    const box = boxOf(s.boards);
    if (box) holes.push({ x: box.x, y: s.yStart, width: box.width, height: s.height });
  }
  for (const o of layout.openings ?? []) holes.push({ x: o.x, y: o.y, width: o.width, height: o.height });
  return subtractRects(whole, holes).filter(bigEnough);
}

/** A flat ceiling's bare rectangles (x along the room's length, y along its width). */
export function bareCeilingRects(layout: CeilingBoardLayout): Rect[] {
  const whole: Rect = { x: 0, y: 0, width: layout.length, height: layout.width };
  const box = boxOf(layout.boards);
  return subtractRects(whole, box ? [box] : []).filter(bigEnough);
}

// ---------------------------------------------------------------------------
// Textures (canvas, tiled in feet)
// ---------------------------------------------------------------------------

const textures = new Map<string, THREE.CanvasTexture>();

function canvasTexture(id: string, size: number, tileFt: number, draw: (ctx: CanvasRenderingContext2D, px: number, size: number) => void): THREE.CanvasTexture {
  const cached = textures.get(id);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) draw(ctx, size / tileFt, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  textures.set(id, texture);
  return texture;
}

/** A repeatable pseudo-random in [0, 1). */
const rnd = (seed: number) => grainOffset(seed * 0.618, seed * 1.37);

function mottle(ctx: CanvasRenderingContext2D, size: number, count: number, alpha: number, seed: number): void {
  for (let i = 0; i < count; i++) {
    const x = rnd(seed + i * 3) * size, y = rnd(seed + i * 3 + 1) * size;
    const r = 4 + rnd(seed + i * 3 + 2) * 24;
    const dark = rnd(seed + i * 7) > 0.5;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(${dark ? '0,0,0' : '255,255,255'},${alpha})`);
    g.addColorStop(1, `rgba(${dark ? '0,0,0' : '255,255,255'},0)`);
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
  }
}

/** Unpainted drywall: 4 × 8 sheets hung sideways, joints mudded and taped, screws every foot on the studs. 8 ft tile. */
export function drywallTexture(): THREE.CanvasTexture {
  return canvasTexture('bare-drywall', 1024, 8, (ctx, px, size) => {
    const ft = (f: number) => f * px;
    ctx.fillStyle = '#d4d0c9';
    ctx.fillRect(0, 0, size, size);
    mottle(ctx, size, 1400, 0.05, 11);
    // Feathered mud over a joint: a soft band, the tape a crisper strip inside it.
    const band = (x: number, y: number, w: number, h: number, vertical: boolean) => {
      const g = vertical ? ctx.createLinearGradient(x, 0, x + w, 0) : ctx.createLinearGradient(0, y, 0, y + h);
      g.addColorStop(0, 'rgba(228,224,217,0)');
      g.addColorStop(0.3, 'rgba(228,224,217,0.9)');
      g.addColorStop(0.7, 'rgba(228,224,217,0.9)');
      g.addColorStop(1, 'rgba(228,224,217,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = 'rgba(214,210,203,0.55)';
      if (vertical) ctx.fillRect(x + w / 2 - ft(0.08), y, ft(0.16), h);
      else ctx.fillRect(x, y + h / 2 - ft(0.08), w, ft(0.16));
    };
    // The long joints: at 4 ft and at the tile's edge (0 / 8 ft), 12" of mud.
    band(0, ft(4) - ft(0.5), size, ft(1), false);
    band(0, -ft(0.5), size, ft(1), false);
    band(0, size - ft(0.5), size, ft(1), false);
    // Butt joints, staggered: the upper sheet's at the tile edge, the lower one's at 4 ft. 8" of mud.
    band(-ft(0.33), 0, ft(0.66), ft(4), true);
    band(size - ft(0.33), 0, ft(0.66), ft(4), true);
    band(ft(4) - ft(0.33), ft(4), ft(0.66), ft(4), true);
    // Screws: on the studs (16" o.c.), about every 12", each a dimple with a dab of mud.
    for (let col = 0; col < 6; col++) {
      for (let row = 0; row < 8; row++) {
        const x = ft(col * STUD_SPACING) + (rnd(col * 31 + row) - 0.5) * ft(0.04);
        const y = ft(row + 0.5) + (rnd(col * 17 + row * 5) - 0.5) * ft(0.15);
        const spot = ctx.createRadialGradient(x, y, 0, x, y, ft(0.12));
        spot.addColorStop(0, 'rgba(230,226,219,0.8)');
        spot.addColorStop(1, 'rgba(230,226,219,0)');
        ctx.fillStyle = spot;
        ctx.fillRect(x - ft(0.12), y - ft(0.12), ft(0.24), ft(0.24));
        ctx.fillStyle = 'rgba(70,66,60,0.45)';
        ctx.beginPath();
        ctx.arc(x, y, ft(0.018), 0, Math.PI * 2);
        ctx.fill();
      }
    }
  });
}

/** The paper back of a sheet of drywall: brown-grey, a little fibrous. 4 ft tile. */
export function backingTexture(): THREE.CanvasTexture {
  return canvasTexture('bare-backing', 256, 4, (ctx, px, size) => {
    ctx.fillStyle = '#9f978c';
    ctx.fillRect(0, 0, size, size);
    mottle(ctx, size, 500, 0.06, 23);
    ctx.strokeStyle = 'rgba(0,0,0,0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 90; i++) {
      const y = rnd(i * 5 + 100) * size;
      const x = rnd(i * 5 + 101) * size;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rnd(i * 5 + 102) - 0.5) * 60, y + (rnd(i * 5 + 103) - 0.5) * 6);
      ctx.stroke();
    }
    // The sheet's edge, every 4 ft.
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.fillRect(0, 0, size, 2);
  });
}

/** A neutral grid: a foot square, with 6" ticks. 1 ft tile. */
export function gridTexture(): THREE.CanvasTexture {
  return canvasTexture('bare-grid', 128, 1, (ctx, px, size) => {
    ctx.fillStyle = '#8f949a';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#7b8086';
    ctx.fillRect(size / 2, 0, 1, size);
    ctx.fillRect(0, size / 2, size, 1);
    ctx.fillStyle = '#5c6167';
    ctx.fillRect(0, 0, size, 2);
    ctx.fillRect(0, 0, 2, size);
  });
}

// ---------------------------------------------------------------------------
// Materials and geometry
// ---------------------------------------------------------------------------

/** The sheet's material: a new one per wall (each wall's opacity is its own); the texture shared. Exported as `drywall`. */
function sheetMaterial(kind: 'drywall' | 'backing' | 'grid'): THREE.MeshStandardMaterial {
  const map = kind === 'drywall' ? drywallTexture() : kind === 'backing' ? backingTexture() : gridTexture();
  // The colour is what the export carries when the picture does not: the sheet's own tone.
  const color = kind === 'drywall' ? 0xd4d0c9 : kind === 'backing' ? 0x9f978c : 0x8f949a;
  const material = new THREE.MeshStandardMaterial({ map, color: 0xffffff, roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
  material.userData.kind = 'drywall';
  material.userData.exportColor = `#${new THREE.Color(color).getHexString()}`;
  return material;
}

/** A rectangle of a wall (surface coordinates) as a quad at depth `z`, its UVs in tiles of `tileFt`, facing +z. */
function wallQuad(r: Rect, z: number, tileFt: number): THREE.BufferGeometry {
  const x0 = r.x, x1 = r.x + r.width, y0 = r.y, y1 = r.y + r.height;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([x0, y0, z, x1, y0, z, x1, y1, z, x0, y1, z], 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1], 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute([x0 / tileFt, y0 / tileFt, x1 / tileFt, y0 / tileFt, x1 / tileFt, y1 / tileFt, x0 / tileFt, y1 / tileFt], 2));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  return g;
}

/** A rectangle of a flat ceiling (x along the length, y along the width) as a quad at height `y`, facing down. */
function ceilingQuad(r: Rect, y: number, tileFt: number): THREE.BufferGeometry {
  const x0 = r.x, x1 = r.x + r.width, z0 = r.y, z1 = r.y + r.height;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([x0, y, z0, x0, y, z1, x1, y, z1, x1, y, z0], 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute([0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0], 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute([x0 / tileFt, z0 / tileFt, x0 / tileFt, z1 / tileFt, x1 / tileFt, z1 / tileFt, x1 / tileFt, z0 / tileFt], 2));
  // Wound so the face's own normal points DOWN (seen from the room): a
  // renderer that culls back faces (Blender, from a single-sided export)
  // would otherwise look straight through it.
  g.setIndex([0, 2, 1, 0, 3, 2]);
  return g;
}

function sheetMesh(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  return mesh;
}

/** A stick of lumber as a box, its position baked into the vertices, the grain along its length. */
function stick(group: THREE.Group, material: THREE.Material, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number): void {
  if (x1 - x0 < MIN_STICK || y1 - y0 < MIN_STICK || z1 - z0 < MIN_STICK) return;
  const geometry = new THREE.BoxGeometry(x1 - x0, y1 - y0, z1 - z0);
  const longest = Math.max(x1 - x0, y1 - y0, z1 - z0);
  setGrainUVs(geometry, longest === y1 - y0 ? 'y' : longest === x1 - x0 ? 'x' : 'z', grainOffset(x0 + z0, y0));
  geometry.translate((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
}

/** A wall's bare rectangles with the ones at the top run on above the wall (the rim that meets the ceiling's sheet). */
function withRim(rects: Rect[], wallHeight: number): Rect[] {
  return rects.map((r) => (r.y + r.height > wallHeight - EPS ? { ...r, height: r.height + RIM } : r));
}

/** A ceiling's bare rectangles with the ones at the room's edges run out past it (to meet the walls' sheets). */
function withReach(rects: Rect[], length: number, width: number): Rect[] {
  return rects.map((r) => {
    let { x, y, width: w, height: h } = r;
    if (x < EPS) { x -= RIM; w += RIM; }
    if (x + w > length - EPS) w += RIM;
    if (y < EPS) { y -= RIM; h += RIM; }
    if (y + h > width - EPS) h += RIM;
    return { x, y, width: w, height: h };
  });
}

/**
 * The joists over a bare stretch of a flat ceiling: across the room's width,
 * on the 16" grid along its length. A flat ceiling has no framing of its own
 * (framingBuilder frames only sloped ceilings), so these are NOT hidden with
 * the walls' bare studs when the framing is shown: nothing would stand in for them.
 */
function ceilingJoists(rects: Rect[], roomHeight: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'bare-joists';
  const lumber = createLumberMaterial();
  const y0 = roomHeight, y1 = roomHeight + STUD_DEPTH;
  for (const r of rects) {
    const x0 = r.x, x1 = r.x + r.width, zz0 = r.y, zz1 = r.y + r.height;
    for (let k = Math.max(0, Math.floor(x0 / STUD_SPACING)); k * STUD_SPACING < x1 + STUD_THICKNESS; k++) {
      const c = k * STUD_SPACING;
      stick(group, lumber, Math.max(x0, c - STUD_THICKNESS / 2), Math.min(x1, c + STUD_THICKNESS / 2), y0, y1, zz0, zz1);
    }
  }
  return group;
}

// ---------------------------------------------------------------------------
// The fills
// ---------------------------------------------------------------------------

/**
 * What a wall shows where it is bare, placed on its face of the room (the
 * caller cuts it to a sloped top). Null when nothing of the wall is bare.
 */
export function buildWallBareFill(layout: WallBoardLayout, fill: BareFillId, face: WallFace, roomLength: number, roomWidth: number): THREE.Group | null {
  const rects = bareRects(layout);
  if (rects.length === 0) return null;
  const group = new THREE.Group();
  group.name = 'bare-fill';
  group.userData.bareFill = fill;
  // The sheets: built in canonical coordinates, positions in the vertices, turned onto the face.
  const sheets = new THREE.Group();
  sheets.name = 'bare-sheets';
  if (fill === 'studs') {
    const backing = sheetMaterial('backing');
    for (const r of withRim(rects, layout.wallHeight)) sheets.add(sheetMesh(wallQuad(r, -BACKING_OFFSET, 4), backing));
  } else {
    const material = sheetMaterial(fill);
    const tile = fill === 'drywall' ? 8 : 1;
    for (const r of withRim(rects, layout.wallHeight)) sheets.add(sheetMesh(wallQuad(r, 0, tile), material));
  }
  applyFaceTransformToGroup(sheets, face, roomLength, roomWidth);
  group.add(sheets);
  // The framing where the wall is bare: the room's own, cut to the bare areas.
  if (fill === 'studs') {
    group.add(buildWallBareFraming(face, layout.wallLength, layout.wallHeight, layout.openings ?? [], rects, layout.outline, roomLength, roomWidth));
  }
  return group;
}

/** What a flat ceiling shows where it is bare, in room coordinates (the ceiling plane at `roomHeight`). */
export function buildCeilingBareFill(layout: CeilingBoardLayout, fill: BareFillId, roomHeight: number): THREE.Group | null {
  const rects = bareCeilingRects(layout);
  if (rects.length === 0) return null;
  const group = new THREE.Group();
  group.name = 'bare-fill';
  group.userData.bareFill = fill;
  if (fill === 'studs') {
    const backing = sheetMaterial('backing');
    for (const r of withReach(rects, layout.length, layout.width)) group.add(sheetMesh(ceilingQuad(r, roomHeight + BACKING_OFFSET, 4), backing));
    group.add(ceilingJoists(rects, roomHeight));
  } else {
    const material = sheetMaterial(fill);
    const tile = fill === 'drywall' ? 8 : 1;
    for (const r of rects) group.add(sheetMesh(ceilingQuad(r, roomHeight, tile), material));
  }
  return group;
}

/** Show or hide the bare areas' own studs under `root`: hidden while the framing is shown (the framing has them). */
export function setBareStudsVisible(root: THREE.Object3D, visible: boolean): void {
  root.traverse((o) => { if (o.userData.bareStuds) o.visible = visible; });
}
